import { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import API from '@api/api';
import { AppContext } from '@utils/AppContext';

export const MaterialManageContext = createContext();

// 每行8个，加载4行
const PAGE_SIZE = 8 * 4;

export const MaterialManageContextProvider = ({ children }) => {
    // 左侧菜单相关
    const [activeTab, setActiveTab] = useState('personal') // 当前tab：personal/enterprise
    const [menuList, setMenuList] = useState([]) // 菜单列表
    const [menuTotal, setMenuTotal] = useState(0) // 菜单总素材数
    const [menuLoading, setMenuLoading] = useState(false) // 菜单加载状态
    const [activeMenu, setActiveMenu] = useState(null) // 当前选中的菜单
    const [activeMenuName, setActiveMenuName] = useState('全部参考图') // 当前选中的菜单名称
    const [expandedKeys, setExpandedKeys] = useState([]) // 树展开的节点

    // 右侧列表相关
    const [materialList, setMaterialList] = useState([]) // 素材列表
    const [total, setTotal] = useState(0) // 素材总数
    const [loading, setLoading] = useState(false) // 加载状态
    const [loadingMore, setLoadingMore] = useState(false) // 加载更多状态
    const [searchKeyword, setSearchKeyword] = useState('') // 搜索关键词
    const [pageIndex, setPageIndex] = useState(1) // 当前页码
    const [hasMore, setHasMore] = useState(true) // 是否有更多数据
    const fetchIdRef = useRef(0); // 防止竞态

    // 批量操作
    const [batchMode, setBatchMode] = useState(false) // 是否批量操作模式
    const [selectedList, setSelectedList] = useState([]) // 已选中的素材

    // 获取文件夹树
    const fetchFolderTree = useCallback((scope) => {
        setMenuLoading(true);
        API.materialFolderTree({ scope }) 
            .then((res) => {
                if (res.code === 0 && Array.isArray(res.data)) {
                    setMenuList(res.data);
                    setMenuTotal(res.attr?.total ?? 0);
                } else {
                    setMenuList([]);
                    setMenuTotal(0);
                }
            })
            .catch(() => {
                setMenuList([]);
                setMenuTotal(0);
            })
            .finally(() => {
                setMenuLoading(false);
            });
    }, []);

    // 递归查找文件夹的所有祖先节点 ID
    const findAncestorKeys = (list, targetId, path = []) => {
        for (const node of list) {
            if (node.id === targetId) {
                return path;
            }
            if (node.children && node.children.length > 0) {
                const result = findAncestorKeys(node.children, targetId, [...path, node.id]);
                if (result !== null) {
                    return result;
                }
            }
        }
        return null;
    };

    // 选中文件夹时，自动展开父级链
    // ??????????????
    const selectFolderWithExpand = useCallback((folderId, folderName) => {
        // ??????
        const ancestorKeys = findAncestorKeys(menuList, folderId);
        if (ancestorKeys && ancestorKeys.length > 0) {
            // ????????????????
            setExpandedKeys(prev => {
                const newKeys = [...new Set([...prev, ...ancestorKeys])];
                return newKeys;
            });
        }
        // ????????????????????
        setMaterialList([]);
        // 切文件夹时先锁住加载更多，避免滚动监听误触发 pageIndex=2
        setLoading(true);
        setLoadingMore(false);
        setPageIndex(1);
        setHasMore(true);
        // ?????????? useEffect ??? pageIndex=1 ??
        setActiveMenu(folderId);
        setActiveMenuName(folderName);
    }, [menuList, setActiveMenu, setActiveMenuName]);

    // 获取素材列表
    const fetchMaterialList = useCallback((params = {}, append = false) => {
        const {
            scope = 'personal',
            folderId = '',
            keyword = '',
            pageIndex: pi = 1,
        } = params;

        const isAllRef = !folderId; // 全部参考图时 folderId 为空

        const fetchId = ++fetchIdRef.current;

        if (append) {
            setLoadingMore(true);
        } else {
            setLoading(true);
        }

        API.materialList({
            scope,
            folderId,
            keyword,
            pageIndex: pi,
            pageSize: PAGE_SIZE,
            withAttr: false,
        })
            .then((res) => {
                if (fetchId !== fetchIdRef.current) return; // 防止竞态
                if (res.code === 0 && res.data) {
                    const { subFolders = [], images = {} } = res.data;
                    const imageItems = Array.isArray(images.items) ? images.items : [];
                    const totalImages = images.total ?? 0;

                    // subFolders 标记 type='folder'，images 标记 type='image'
                    const folderList = subFolders.map(f => ({ ...f, type: 'folder' }));
                    const imageList = imageItems.map(img => ({ ...img, type: 'image', url: img.imageUrl }));

                    if (append) {
                        // 加载更多只追加图片（文件夹只在首页返回）
                        setMaterialList(prev => [...prev, ...imageList]);
                    } else {
                        // 首页：文件夹 + 图片
                        setMaterialList([...folderList, ...imageList]);
                    }
                    setTotal(subFolders.length + totalImages);
                    setPageIndex(pi);
                    setHasMore(pi * PAGE_SIZE < totalImages);
                } else {
                    if (!append) {
                        setMaterialList([]);
                        setTotal(0);
                    }
                    setHasMore(false);
                }
            })
            .catch(() => {
                if (fetchId !== fetchIdRef.current) return;
                if (!append) {
                    setMaterialList([]);
                    setTotal(0);
                }
                setHasMore(false);
            })
            .finally(() => {
                if (fetchId !== fetchIdRef.current) return;
                setLoading(false);
                setLoadingMore(false);
            });
    }, []);

    // 加载下一页
    const loadMoreMaterial = useCallback(() => {
        // 首屏加载中或已无更多数据时，禁止触发下一页请求
        if (loading || loadingMore || !hasMore) return;
        const nextPage = pageIndex + 1;
        const folderId = activeMenu === 'refImage_all' ? '' : (activeMenu || '');
        fetchMaterialList({
            scope: activeTab,
            folderId,
            keyword: searchKeyword,
            pageIndex: nextPage,
        }, true);
    }, [loading, loadingMore, hasMore, pageIndex, activeMenu, activeTab, searchKeyword, fetchMaterialList]);

    // 切换菜单/tab/搜索时重新拉取素材列表
    useEffect(() => {
        if (!activeMenu) return;
        const folderId = activeMenu === 'refImage_all' ? '' : activeMenu;
        setMaterialList([]);
        setHasMore(true);
        fetchMaterialList({
            scope: activeTab,
            folderId,
            keyword: searchKeyword,
        });
    }, [activeMenu, activeTab, searchKeyword, fetchMaterialList]);

    // 切换 tab 时重新拉取文件夹树
    useEffect(() => {
        fetchFolderTree(activeTab);
    }, [activeTab, fetchFolderTree]);

    return (
        <MaterialManageContext.Provider value={{
            activeTab, setActiveTab,
            menuList, setMenuList,
            menuTotal,
            menuLoading,
            fetchFolderTree,
            activeMenu, setActiveMenu,
            activeMenuName, setActiveMenuName,
            expandedKeys, setExpandedKeys,
            selectFolderWithExpand,
            materialList, setMaterialList,
            total, setTotal,
            loading, setLoading,
            loadingMore,
            hasMore,
            loadMoreMaterial,
            fetchMaterialList,
            pageIndex, setPageIndex,
            searchKeyword, setSearchKeyword,
            batchMode, setBatchMode,
            selectedList, setSelectedList,
        }}>
            {children}
        </MaterialManageContext.Provider>
    );
};
